/**
 * D-EZ-109 — WHAT THE WIRE READS.
 *
 * The league's seasons (settled and live, as `HistorySeason`), each season's
 * calendar (to name next week's race — a calendar is printed before the year
 * starts, so knowing it is not knowing a result), and whatever sponsor books the
 * season still holds. `wireLeague` builds it from what the shell holds; nothing
 * here is stored.
 *
 * ⚠ THE SPONSOR BOOKS ARE ONLY WHAT IS HELD. A live season holds every
 * contract and every week's sponsor comings and goings. A settled season's
 * archive keeps the contracts that survived the winter renewals and drops the
 * weekly log (`lib/seasonArchive.ts`), so a settled season's Wires read its
 * contracts only — always, even from a copy that still has the log, so a back
 * Wire prints the same before and after a save. It carries less sponsor news
 * than the live one did (no one-race specials, no warnings, no deals the winter
 * did not renew) — never invented news.
 */

import type { HistorySeason } from '../history/source.ts';
import { sortedSeasons, liveHistorySeason } from '../history/source.ts';
import type { ScheduledRace } from '../schedule.ts';
import type { DriverFinanceState, SponsorChange } from '../finance/raceFinance.ts';
import type { SeasonState } from '../seasonPlay.ts';
import type { CareerPrior } from '../history/types.ts';

export interface WireBooks {
  /** The cars' sponsorship contracts, by driver (`sponsorSlotsFor` reads one). */
  contracts?: Readonly<Record<string, DriverFinanceState>> | null;
  /** The season's sponsor comings and goings, by race. Held while a season is live. */
  sponsorChanges?: readonly (SponsorChange & { raceNumber: number })[] | null;
  /** True when the contracts are the end-of-year books (after the winter renewals). */
  settled?: boolean;
}

export interface WireSeason {
  history: HistorySeason;
  /** The season's calendar, race 1 first. */
  schedule: readonly ScheduledRace[];
  books?: WireBooks | null;
}

export interface WireLeague {
  seasons: WireSeason[];
  /** Pre-league careers (D-EZ-97), for "his 30th career win" on a 2025 roster. */
  priors?: Readonly<Record<string, CareerPrior>>;
}

/** A settled season, as the shell holds one (a `CareerSeason` fits). */
export interface SettledSeasonLike extends HistorySeason {
  season: HistorySeason['season'] & { schedule?: { races: readonly ScheduledRace[] } };
  finance?: HistorySeason['finance'] & {
    state?: { byDriver: Readonly<Record<string, DriverFinanceState>> };
    sponsorChanges?: readonly (SponsorChange & { raceNumber: number })[];
  };
}

/** The live season, as the shell holds it. */
export interface LiveSeasonLike {
  seasonNumber: number;
  roster: HistorySeason['roster'];
  teams: HistorySeason['teams'];
  state: SeasonState;
}

/**
 * The league as the Wire reads it, from the shelf: settled seasons (history and
 * the workbench) and the season being raced.
 */
export function wireLeague(shelf: {
  history: readonly SettledSeasonLike[];
  career?: SettledSeasonLike | null;
  live?: LiveSeasonLike | null;
}): WireLeague {
  const byNumber = new Map<number, WireSeason>();
  const settled = [...shelf.history, ...(shelf.career ? [shelf.career] : [])];
  for (const s of settled) {
    byNumber.set(s.seasonNumber, {
      history: s,
      schedule: s.season.schedule?.races ?? s.season.races.map((r) => r.scheduled),
      books: s.finance?.state
        ? { contracts: s.finance.state.byDriver, sponsorChanges: s.finance.sponsorChanges ?? null, settled: true }
        : null,
    });
  }
  const live = shelf.live;
  if (live && !byNumber.has(live.seasonNumber) && live.state.races.length > 0) {
    const h = liveHistorySeason(live);
    byNumber.set(live.seasonNumber, {
      // The bracket as far as it has been decided, so a live Wire reads the
      // playoff the same way the settled season's back Wire will.
      history: {
        ...h,
        season: {
          ...h.season,
          playoff: { seeds: live.state.playoff.seeds, rounds: live.state.playoff.rounds, finalFour: [], championId: null, championRacePoints: null },
        },
      },
      schedule: live.state.schedule.races,
      books: live.state.finance
        ? { contracts: live.state.finance.contracts.byDriver, sponsorChanges: live.state.finance.sponsorChanges, settled: false }
        : null,
    });
  }
  const seasons = sortedSeasons([...byNumber.values()].map((w) => w.history)).map((h) => byNumber.get(h.seasonNumber) as WireSeason);
  const priors =
    live?.state.inherited?.priors ?? [...settled].sort((a, b) => b.seasonNumber - a.seasonNumber).find((s) => s.offseason?.carry)?.offseason?.carry?.priors;
  return { seasons, ...(priors ? { priors } : {}) };
}

/** The weeks a season has a Wire for: after race 1 … after the second-to-last race run. */
export function wireWeeks(league: WireLeague, seasonNumber: number): number[] {
  const s = league.seasons.find((w) => w.history.seasonNumber === seasonNumber);
  if (!s) return [];
  const run = s.history.season.races.map((r) => r.raceNumber).sort((a, b) => a - b);
  const last = s.schedule.length;
  return run.filter((n) => n < last);
}
